import { useState, useEffect, useRef } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import Header from './partials/Header';
import AgencyDetails, { AgencyError, AgencyLoading } from './agency/AgencyDetails';
import { isPublicAgencyId } from './utils/agencyUrl';
import { trackInteraction } from './utils/interactions';

const API_AUTH_URL = process.env.REACT_APP_AUTH_API_URL;

export default function Info() {
    const { publicId } = useParams();
    if (!isPublicAgencyId(publicId)) return <Navigate to="/404" replace />;
    return <AgencyInfo key={publicId} publicId={publicId} />;
}

function AgencyInfo({ publicId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [attempt, setAttempt] = useState(0);
    const viewTracked = useRef(false);

    useEffect(() => {
        let currentRequest;
        const fetchData = async () => {
            if (currentRequest && !currentRequest.signal.aborted) return;
            const controller = new AbortController();
            currentRequest = controller;
            setLoading(true);
            setError(null);
            setData(null);
            try {
                const response = await fetch(`${API_AUTH_URL}/auth/getFuneralHonorData/${publicId}`, {
                    signal: controller.signal,
                    cache: 'no-store',
                    credentials: 'omit',
                });
                if (controller.signal.aborted) return;
                if (response.status === 404) {
                    setError('not-found');
                } else if (response.ok) {
                    const result = await response.json();
                    if (controller.signal.aborted) return;
                    if (!result) setError('not-found');
                    else if (result.publicId === publicId) {
                        setData(result);
                        if (!viewTracked.current) {
                            viewTracked.current = true;
                            void trackInteraction({ action: 'view', agencyId: publicId, source: 'agency_detail' });
                        }
                    } else setError('unavailable');
                } else setError('unavailable');
            } catch {
                if (!controller.signal.aborted) setError('unavailable');
            } finally {
                if (!controller.signal.aborted) setLoading(false);
                if (currentRequest === controller) currentRequest = undefined;
            }
        };
        const refreshVisible = () => {
            if (document.visibilityState !== 'hidden') fetchData();
        };
        const visibilityChanged = () => {
            if (document.visibilityState === 'hidden') {
                currentRequest?.abort();
                setData(null);
                setLoading(true);
            } else fetchData();
        };
        fetchData();
        window.addEventListener('focus', refreshVisible);
        window.addEventListener('pageshow', refreshVisible);
        document.addEventListener('visibilitychange', visibilityChanged);
        const interval = window.setInterval(refreshVisible, 60000);
        return () => {
            currentRequest?.abort();
            window.clearInterval(interval);
            window.removeEventListener('focus', refreshVisible);
            window.removeEventListener('pageshow', refreshVisible);
            document.removeEventListener('visibilitychange', visibilityChanged);
        };
    }, [publicId, attempt]);

    if (error === 'not-found') return <Navigate to="/404" replace />;

    return <div className="ef-page ef-agency-page"><Header />{loading ? <AgencyLoading /> : error || !data ? <AgencyError onRetry={() => setAttempt(value => value + 1)} /> : <AgencyDetails data={data} agencyId={publicId} source="agency_detail" />}</div>;
}
